import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Clock, AlertTriangle } from 'lucide-react';
import config from '../config';

// Store timers globally to survive component re-mounts
const globalTimers = {
  timeoutId: null,
  warningTimeoutId: null,
  countdownInterval: null,
  lastActivityTime: Date.now(),
  isWarningShown: false
};

/**
 * Auto Logout Component with Custom Warning Modal
 * Stable across re-renders - timers persist even if component unmounts/remounts
 */
const AutoLogout = ({ timeoutMinutes = 30, warningMinutes = 5 }) => {
  const isInitialized = useRef(false);
  
  const [showWarning, setShowWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(0);

  // Convert minutes to milliseconds
  const TIMEOUT_MS = timeoutMinutes * 60 * 1000;
  const WARNING_MS = (timeoutMinutes - warningMinutes) * 60 * 1000;

  // Logout function - Uses Cognito Hosted UI logout
  const logout = useCallback(() => {
    const logoutUri = encodeURIComponent(`${config.appDomain}/logged-out`);
    window.location.href = `${config.cognitoDomain}/logout?client_id=${config.userPoolWebClientId}&logout_uri=${logoutUri}`;
  }, []);

  // Start countdown timer
  const startCountdown = useCallback(() => {
    setSecondsRemaining(warningMinutes * 60);
    
    if (globalTimers.countdownInterval) {
      clearInterval(globalTimers.countdownInterval);
    }
    
    globalTimers.countdownInterval = setInterval(() => {
      setSecondsRemaining(prev => {
        if (prev <= 1) {
          clearInterval(globalTimers.countdownInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [warningMinutes]);

  // Show warning modal
  const displayWarning = useCallback(() => {
    globalTimers.isWarningShown = true;
    setShowWarning(true);
    startCountdown();
  }, [startCountdown]);

  // Reset inactivity timer
  const resetTimer = useCallback(() => {
    globalTimers.lastActivityTime = Date.now();
    globalTimers.isWarningShown = false;
    setShowWarning(false);

    // Clear existing timers
    if (globalTimers.timeoutId) clearTimeout(globalTimers.timeoutId);
    if (globalTimers.warningTimeoutId) clearTimeout(globalTimers.warningTimeoutId);
    if (globalTimers.countdownInterval) clearInterval(globalTimers.countdownInterval);

    // Set warning timer
    if (warningMinutes > 0) {
      globalTimers.warningTimeoutId = setTimeout(() => {
        displayWarning();
      }, WARNING_MS);
    }

    // Set logout timer
    globalTimers.timeoutId = setTimeout(() => {
      logout();
    }, TIMEOUT_MS);
  }, [TIMEOUT_MS, WARNING_MS, warningMinutes, displayWarning, logout]);

  // Activity events to track
  const events = [
    'mousedown',
    'mousemove',
    'keypress',
    'scroll',
    'touchstart',
    'click'
  ];

  // Handle user activity
  const handleActivity = useCallback(() => {
    // Don't reset timer if warning is showing - user must click "Stay Logged In"
    if (globalTimers.isWarningShown) return;
    
    const now = Date.now();
    // Throttle - only reset if it's been at least 1 second since last activity
    if (now - globalTimers.lastActivityTime > 1000) {
      resetTimer();
    }
  }, [resetTimer]);

  // Handle "Stay Logged In" button
  const handleStayLoggedIn = () => {
    resetTimer();
  };

  useEffect(() => {
    // Only initialize once globally
    if (!isInitialized.current) {
      isInitialized.current = true;
      resetTimer();

      events.forEach(event => {
        window.addEventListener(event, handleActivity);
      });
    } else {
      // Sync state with global state
      setShowWarning(globalTimers.isWarningShown);
    }

    // Cleanup only removes event listeners, keeps timers running
    return () => {
      // DON'T clear timers on unmount - let them persist
      // Only remove event listeners to avoid duplicates
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, []); // Empty deps - only run once

  // Re-add event listeners if component remounts
  useEffect(() => {
    events.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [handleActivity]);

  // Format seconds as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!showWarning) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-yellow-100 rounded-full">
            <AlertTriangle className="text-yellow-600" size={24} />
          </div>
          <h3 className="text-xl font-bold text-gray-800">Session Expiring Soon</h3>
        </div>

        <div className="mb-6">
          <p className="text-gray-600 mb-4">
            You will be logged out due to inactivity.
          </p>
          
          <div className="flex items-center justify-center gap-2 p-4 bg-gray-50 rounded-lg">
            <Clock className="text-gray-600" size={20} />
            <span className="text-2xl font-mono font-bold text-gray-800">
              {formatTime(secondsRemaining)}
            </span>
          </div>
          
          <p className="text-sm text-gray-500 text-center mt-3">
            Click below to stay logged in
          </p>
        </div>

        <div className="flex gap-3">
          <button
            onClick={handleStayLoggedIn}
            className="flex-1 bg-spruce-600 text-white py-2 px-4 rounded-lg hover:bg-spruce-700 transition font-semibold"
          >
            Stay Logged In
          </button>
          <button
            onClick={logout}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
          >
            Logout Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default AutoLogout;